import React, { useState, useEffect, useCallback } from 'react';
import { formatDateOrNA } from '../utils/formatDate';
import axios from 'axios';
import {
  Typography,
  Box,
  Paper,
  Divider,
  Button,
  CircularProgress,
  TextField,
  Grid,
  InputAdornment,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  FilterAlt as FilterIcon,
  Add as AddIcon,
  RequestPage as RequestPageIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { hasPermission } from '../utils/authUtils';
import EditBackgroundCheckDialog from '../components/Submissions/EditBackgroundCheckDialog'; // Import the edit dialog component
import CreateBackgroundCheckDialog from './components/CreateBackgroundCheckDialog'; // Import the create dialog component
import { useNotification } from '../context/NotificationContext';

function BackgroundChecksSettings() {
  const { showNotification } = useNotification();
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // State for the edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBackgroundCheckForEdit, setSelectedBackgroundCheckForEdit] = useState(null);
  
  // State for the create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogMode, setCreateDialogMode] = useState('create'); // 'create' or 'request'
  
  // State for the delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBackgroundCheckForDelete, setSelectedBackgroundCheckForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Filter options come from the background-check records themselves and are
  // narrowed by the current company / vendor selection
  const [filterOptions, setFilterOptions] = useState({
    companies: [], vendors: [], firstNames: [], lastNames: [], countries: [], cities: [], states: [], zipCodes: [], statuses: []
  });
  const statusOptions = ['Not Started', 'Requested', 'Document Uploaded', 'Pending Review', 'Approved', 'Denied', 'Expired', 'Error'];
  
  // Filters state
  const [filters, setFilters] = useState({
    companyName: null,
    vendorId: null,
    firstName: '',
    lastName: '',
    country: '',
    city: '',
    state: '',
    zipCode: '',
    status: null,
    nearExpiration: false
  });

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Fetch background checks with filters
  const fetchBackgroundChecks = useCallback(async (pageNum = 0, rowsPerPageNum = 10, filterParams = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      
      // Prepare filter parameters
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', pageNum + 1); // API uses 1-based indexing
      params.append('limit', rowsPerPageNum);
      
      // Add filters
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== null && value !== '' && value !== false) {
          params.append(key, value);
        }
      });
      
      // Special handling for nearExpiration filter
      if (filterParams.nearExpiration) {
        const today = new Date();
        const ninetyDaysFromNow = new Date();
        ninetyDaysFromNow.setDate(today.getDate() + 90);
        params.append('expirationBefore', ninetyDaysFromNow.toISOString().split('T')[0]);
        params.append('expirationAfter', today.toISOString().split('T')[0]);
      }
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/background-checks-master?${params.toString()}`, { headers });
      
      setBackgroundChecks(response.data.records || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching background checks:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load background checks. Please try again.';
      setError(errorMessage);
      showNotification(`Error loading background checks: ${errorMessage}`, 'error');
      if (err.response && err.response.status === 401) window.location.href = '/login';
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch filter options scoped to the selected company / vendor
  const fetchFilterOptions = useCallback(async (companyName, vendorId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const params = new URLSearchParams();
      if (companyName) params.append('companyName', companyName);
      if (vendorId) params.append('vendorId', vendorId);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/background-checks-master/filter-options?${params.toString()}`, { headers });
      setFilterOptions(response.data || {});
    } catch (err) {
      console.error('Error fetching filter options:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load filter options. Please try again.';
      setError(errorMessage);
      showNotification(`Error loading filter options: ${errorMessage}`, 'error');
    }
  }, [getAuthHeader]);

  // Initial data load
  useEffect(() => {
    fetchBackgroundChecks(page, rowsPerPage, filters);
  }, [fetchBackgroundChecks, page, rowsPerPage]);

  useEffect(() => {
    fetchFilterOptions(filters.companyName, filters.vendorId);
  }, [fetchFilterOptions, filters.companyName, filters.vendorId]);

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    let filterValue = value;
    const updatedFilters = { ...filters };

    if (name === 'companyName') {
      filterValue = value === 'All Companies' ? null : value;
      // A vendor from another company no longer applies
      if (filterValue && filters.vendorId) {
        const vendor = filterOptions.vendors.find((v) => v.vendorIdentifier === filters.vendorId);
        if (vendor && vendor.companyName !== filterValue) updatedFilters.vendorId = null;
      }
    } else if (name === 'vendorId') {
      filterValue = value?.companyName === 'All Vendors' ? null : (value ? value.vendorIdentifier : null);
      // Choosing a vendor implies its company
      if (value && value.vendorIdentifier && value.companyName) updatedFilters.companyName = value.companyName;
    }

    updatedFilters[name] = filterValue;
    setFilters(updatedFilters);
    setPage(0); // Reset to first page when filters change
    fetchBackgroundChecks(0, rowsPerPage, updatedFilters);
  };

  const companyOptions = ['All Companies', ...(filterOptions.companies || [])];
  const vendorOptions = [{ vendorIdentifier: null, companyName: 'All Vendors' }, ...(filterOptions.vendors || [])];

  // Free-text filter that also suggests the values present in the current selection
  const renderTextFilter = (name, label, options) => (
    <Autocomplete
      freeSolo
      options={options || []}
      inputValue={filters[name] || ''}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'reset' && !newValue) return;
        handleFilterChange(name, newValue || '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
      disabled={loading}
    />
  );

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchBackgroundChecks(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchBackgroundChecks(0, newRowsPerPage, filters);
  };

  // Format date for display
  const formatDate = formatDateOrNA;

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Denied': return 'error';
      case 'Pending Review': return 'warning';
      case 'Expired': return 'error';
      case 'Requested': return 'info';
      case 'Error': return 'error';
      default: return 'default';
    }
  };

  // Check if a background check is near expiration
  const isNearExpiration = (expirationDate) => {
    if (!expirationDate) return false;
    
    const today = new Date();
    const expiration = new Date(expirationDate);
    const ninetyDaysFromNow = new Date();
    ninetyDaysFromNow.setDate(today.getDate() + 90);
    
    return expiration <= ninetyDaysFromNow && expiration >= today;
  };

  // View background check details
  const handleViewDetails = (backgroundCheck) => {
    if (!backgroundCheck.documentPath) {
      const errorMessage = 'No document available to view';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return;
    }
    
    // Construct URL to the new document endpoint
    const headers = getAuthHeader();
    if (!headers) return;
    
    const documentUrl = `${process.env.REACT_APP_API_URL}/api/background-checks-master/${backgroundCheck._id}/document`;

    // For viewing, we need to handle authentication. We'll fetch the document and create a blob URL
    fetch(documentUrl, {
      method: 'GET',
      headers: headers
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      return response.blob();
    })
    .then(blob => {
      // Create blob URL and open in new tab
      const blobUrl = window.URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay to allow the new window to load
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    })
    .catch(error => {
      console.error('Error viewing document:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to load document. Please try again.';
      setError(errorMessage);
      showNotification(`Error viewing document: ${errorMessage}`, 'error');
    });
  };

  // Download background check document
  const handleDownloadDocument = (backgroundCheck) => {
    if (!backgroundCheck.documentPath) {
      const errorMessage = 'No document available for download';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return;
    }
    
    // Construct URL to the new document endpoint with download parameter
    const headers = getAuthHeader();
    if (!headers) return;
    
    const documentUrl = `${process.env.REACT_APP_API_URL}/api/background-checks-master/${backgroundCheck._id}/document?download=true`;
    
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = backgroundCheck.documentOriginalName || 'background-check-document';
    
    // Add authorization header by creating a fetch request instead
    fetch(documentUrl, {
      method: 'GET',
      headers: headers
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      return response.blob();
    })
    .then(blob => {
      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = backgroundCheck.documentOriginalName || 'background-check-document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      console.error('Error downloading document:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to download document. Please try again.';
      setError(errorMessage);
      showNotification(`Error downloading document: ${errorMessage}`, 'error');
    });
  };

  // Handle opening the edit dialog
  const handleEditBackgroundCheck = (backgroundCheck) => {
    if (backgroundCheck?._id) {
      setSelectedBackgroundCheckForEdit(backgroundCheck);
      setEditDialogOpen(true);
    } else {
      console.error('Cannot edit background check: Missing record ID', backgroundCheck);
      const errorMessage = 'Cannot edit background check: Missing required information.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    }
  };

  // Handle closing the edit dialog
  const handleEditDialogClose = () => {
    setSelectedBackgroundCheckForEdit(null);
    setEditDialogOpen(false);
  };

  // Handle successful save from the edit dialog
  const handleEditSaveSuccess = () => {
    // Refresh the background checks list
    fetchBackgroundChecks(page, rowsPerPage, filters);
  };

  // Handle creating a new background check
  const handleCreateBackgroundCheck = () => {
    setCreateDialogMode('create');
    setCreateDialogOpen(true);
  };

  // Handle requesting a background check
  const handleRequestBackgroundCheck = () => {
    setCreateDialogMode('request');
    setCreateDialogOpen(true);
  };

  // Handle closing the create dialog
  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  // Handle successful creation from the create dialog
  const handleCreateSuccess = () => {
    // Refresh the background checks list
    fetchBackgroundChecks(page, rowsPerPage, filters);
  };

  // Handle opening the delete confirmation dialog
  const handleDeleteBackgroundCheck = (backgroundCheck) => {
    if (backgroundCheck?._id) {
      setSelectedBackgroundCheckForDelete(backgroundCheck);
      setDeleteDialogOpen(true);
    } else {
      console.error('Cannot delete background check: Missing record ID', backgroundCheck);
      const errorMessage = 'Cannot delete background check: Missing required information.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    }
  };

  // Handle closing the delete confirmation dialog
  const handleDeleteDialogClose = () => {
    if (!deleteLoading) {
      setSelectedBackgroundCheckForDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  // Handle confirming the delete action
  const handleConfirmDelete = async () => {
    if (!selectedBackgroundCheckForDelete?._id) {
      const errorMessage = 'Cannot delete background check: Missing record ID.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return;
    }

    setDeleteLoading(true);
    setError(null);

    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/background-checks/${selectedBackgroundCheckForDelete._id}`,
        { headers }
      );

      if (response.status === 200) {
        // Success - refresh the list and close dialog
        await fetchBackgroundChecks(page, rowsPerPage, filters);
        setDeleteDialogOpen(false);
        setSelectedBackgroundCheckForDelete(null);
        
        // Show success message
        showNotification('Background check deleted successfully', 'success');
      }
    } catch (err) {
      console.error('Error deleting background check:', err);
      
      let errorMessage;
      if (err.response?.status === 404) {
        errorMessage = 'Background check not found. It may have already been deleted.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this background check.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
        window.location.href = '/login';
      } else {
        errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to delete background check. Please try again.';
      }
      
      setError(errorMessage);
      showNotification(`Error deleting background check: ${errorMessage}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Check if user has permission to access this page
  if (!hasPermission('canViewBackgroundChecks')) {
    return (
      <Box mt={2}>
        <Paper elevation={3} sx={{ padding: 2 }}>
          <Typography variant="h6" color="error">
            You do not have permission to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Background Checks</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasPermission('canCreateBackgroundChecks') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateBackgroundCheck}
                disabled={loading}
              >
                Create Background Check
              </Button>
            )}
            {hasPermission('canRequestBackgroundChecks') && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RequestPageIcon />}
                onClick={handleRequestBackgroundCheck}
                disabled={loading}
              >
                Request Background Check
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* Filter Section */}
        <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filter Background Checks
          </Typography>
          <Grid container spacing={2} alignItems="flex-end">
            {/* Company Name Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={companyOptions}
                getOptionLabel={(option) => option || ''}
                value={filters.companyName || null}
                onChange={(event, newValue) => handleFilterChange('companyName', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company Name"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><SearchIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Vendor ID Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={vendorOptions}
                getOptionLabel={(option) => option.vendorIdentifier ? `${option.vendorIdentifier} (${option.companyName || 'N/A'})` : (option.companyName === 'All Vendors' ? 'All Vendors' : '')}
                value={vendorOptions.find(opt => opt.vendorIdentifier === filters.vendorId) || (filters.vendorId ? { vendorIdentifier: filters.vendorId, companyName: filters.companyName } : null)}
                onChange={(event, newValue) => handleFilterChange('vendorId', newValue)}
                isOptionEqualToValue={(option, value) => option.vendorIdentifier === value?.vendorIdentifier}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor ID/Number"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><SearchIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('firstName', 'First Name', filterOptions.firstNames)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('lastName', 'Last Name', filterOptions.lastNames)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('country', 'Country', filterOptions.countries)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('city', 'City', filterOptions.cities)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('state', 'State', filterOptions.states)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('zipCode', 'Zip Code', filterOptions.zipCodes)}</Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={statusOptions}
                getOptionLabel={(option) => option || ''}
                value={filters.status || null}
                onChange={(event, newValue) => handleFilterChange('status', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><FilterIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Near Expiration Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.nearExpiration}
                    onChange={(e) => handleFilterChange('nearExpiration', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Near Expiration (90 days)"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Error Display */}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Background Checks Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Check Type</TableCell>
                    <TableCell>Date Requested</TableCell>
                    <TableCell>Date Completed</TableCell>
                    <TableCell>Expiration Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backgroundChecks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No background checks found
                      </TableCell>
                    </TableRow>
                  ) : (
                    backgroundChecks.map((check) => (
                      <TableRow key={check._id}>
                        <TableCell>{check.personName || 'N/A'}</TableCell>
                        <TableCell>
                          {check.companyName || 'N/A'}
                          {check.vendorId ? <Typography variant="caption" display="block" color="text.secondary">{check.vendorId}</Typography> : null}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={check.status || 'Not Started'} 
                            color={getStatusColor(check.status)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{check.checkType || 'N/A'}</TableCell>
                        <TableCell>{formatDate(check.dateRequested)}</TableCell>
                        <TableCell>{formatDate(check.dateCompleted)}</TableCell>
                        <TableCell>
                          {formatDate(check.dateExpires)}
                          {isNearExpiration(check.dateExpires) && (
                            <Chip 
                              label="Expiring Soon" 
                              color="warning" 
                              size="small" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {check.documentPath && (
                            <Tooltip title="View Document">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(check)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {check.documentPath && (
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(check)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('canEditBackgroundChecks') && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditBackgroundCheck(check)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('canDeleteBackgroundChecks') && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBackgroundCheck(check)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>
      
      {/* Edit Background Check Dialog */}
      <EditBackgroundCheckDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        record={selectedBackgroundCheckForEdit}
        // Pass application object from selected record
        application={
          selectedBackgroundCheckForEdit
            ? { _id: selectedBackgroundCheckForEdit.application, companyName: selectedBackgroundCheckForEdit.companyName }
            : null
        }
        onSaveSuccess={handleEditSaveSuccess}
      />

      {/* Create Background Check Dialog */}
      <CreateBackgroundCheckDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        onSuccess={handleCreateSuccess}
        mode={createDialogMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title">
          Delete Background Check
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the background check for{' '}
            <strong>{selectedBackgroundCheckForDelete?.personName || 'this person'}</strong>?
            {selectedBackgroundCheckForDelete?.documentPath && (
              <>
                <br />
                <br />
                This will also permanently delete the associated document file.
              </>
            )}
            <br />
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteDialogClose}
            disabled={deleteLoading}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            color="error"
            variant="contained"
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BackgroundChecksSettings;
